import { isNil } from "lodash";
import { useEffect, useState, useTransition } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { useDropdownItemsCtxProvider } from "@ryde/redux";
import {
  bulkApprovalBookingConflictsReasonDropdownItemsAtom,
  IDropdownItem,
  Log,
  ReasonApi,
  transformDropdownDtos,
} from "@ryde/shared";

import { BookingApproveFormFieldsEnum } from "../form-constants";

export const useApprovalErrorFields = ({
  getValues,
  setValue,
}: {
  getValues: UseFormReturn["getValues"];
  setValue: UseFormReturn["setValue"];
}) => {
  const { t } = useTranslation();
  const [reasonCategoryDropdownItems, setReasonCategoryDropdownItems] =
    useState<IDropdownItem[] | undefined>(undefined);
  const { getDropdownItems } = useDropdownItemsCtxProvider();
  const [reasonDropdownItems, setReasonDropdownItems] = useRecoilState(
    bulkApprovalBookingConflictsReasonDropdownItemsAtom,
  );
  const transformedReasonDropdownItems = transformDropdownDtos({
    dtos: reasonDropdownItems,
    entity: "reason",
  });

  const reasonCategoryValue = getValues(
    BookingApproveFormFieldsEnum.APPROVAL_REASON_CATEGORY,
  );

  const reasonValue = getValues(
    BookingApproveFormFieldsEnum.APPROVAL_REASON_ID,
  );
  const [, startTransition] = useTransition();

  useEffect(() => {
    const getReasonCategoryItems = async (): Promise<void> => {
      const reasonCategoryDropDownItems = await getDropdownItems(
        "reasonCategory",
        true,
      );
      setReasonCategoryDropdownItems(reasonCategoryDropDownItems);
      const firstCategory = reasonCategoryDropDownItems?.at(0)?.value ?? null;

      if (!isNil(firstCategory)) {
        setValue(
          BookingApproveFormFieldsEnum.APPROVAL_REASON_CATEGORY,
          firstCategory,
        );
      }
    };

    getReasonCategoryItems();
    // to avoid broken circular update with `getDropdownItems`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getItems = async (): Promise<void> => {
      try {
        const reasonItems =
          await ReasonApi.bookingApprovalDropdownItems(reasonCategoryValue);

        if (!reasonItems) {
          Log.warnAndToast(t("reason.NO_REASON_EXIST"));
          return;
        }

        setReasonDropdownItems(reasonItems);
      } catch (e) {
        Log.error("Error while fetch booking reasons");
      }
    };

    startTransition(() => {
      getItems();
    });
  }, [reasonCategoryValue, setReasonDropdownItems, t]);

  useEffect(() => {
    if (reasonValue) {
      const isReasonValueExists = transformedReasonDropdownItems?.some(
        item => item.value === reasonValue,
      );

      if (!isReasonValueExists) {
        setValue(BookingApproveFormFieldsEnum.APPROVAL_REASON_ID, null);
      }
    }
  }, [
    setValue,
    reasonValue,
    reasonCategoryValue,
    transformedReasonDropdownItems,
  ]);

  return {
    reasonCategoryDropdownItems,
    reasonDropdownItems: transformedReasonDropdownItems,
  };
};
